import Phaser from 'phaser';
import {
  CARD_WIDTH, CARD_HEIGHT, SUITS, SUIT_SYMBOLS, SUIT_COLORS,
  RANK_NAMES, COLORS, GAME_WIDTH, GAME_HEIGHT,
} from '../utils/Constants';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Show loading bar
    const barW = 400;
    const barH = 30;
    const barX = (GAME_WIDTH - barW) / 2;
    const barY = GAME_HEIGHT / 2;

    const bg = this.add.rectangle(GAME_WIDTH / 2, barY, barW, barH, 0x222222);
    const bar = this.add.rectangle(barX + 2, barY, 0, barH - 4, COLORS.HIGHLIGHT);
    bar.setOrigin(0, 0.5);

    const loadText = this.add.text(GAME_WIDTH / 2, barY - 40, 'Loading...', {
      fontSize: '24px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);

    this.load.on('progress', (value: number) => {
      bar.width = (barW - 4) * value;
    });
  }

  create(): void {
    this.generateCardTextures();
    this.scene.start('MenuScene');
  }

  private generateCardTextures(): void {
    // Generate card face textures
    for (const suit of SUITS) {
      for (let rank = 1; rank <= 13; rank++) {
        this.generateCardFace(suit, rank);
      }
    }

    // Generate joker textures
    this.generateJokerFace(0);
    this.generateJokerFace(1);

    // Generate card backs
    this.generateCardBack('red', COLORS.CARD_BACK_RED);
    this.generateCardBack('blue', COLORS.CARD_BACK_BLUE);
  }

  private generateCardFace(suit: string, rank: number): void {
    const key = `card_${suit}_${rank}`;
    const w = CARD_WIDTH;
    const h = CARD_HEIGHT;

    const canvas = this.textures.createCanvas(key, w, h);
    if (!canvas) return;
    const ctx = canvas.context;

    // Card background
    this.drawRoundedRect(ctx, 0, 0, w, h, 6, '#ffffff', '#888888');

    // Suit color
    const color = SUIT_COLORS[suit as keyof typeof SUIT_COLORS];
    const symbol = SUIT_SYMBOLS[suit as keyof typeof SUIT_SYMBOLS];
    const rankName = RANK_NAMES[rank];

    // Top-left rank + suit
    ctx.font = 'bold 14px Georgia, serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.fillText(rankName, 5, 16);
    ctx.font = '15px Georgia, serif';
    ctx.fillText(symbol, 5, 31);

    // Bottom-right rank + suit (rotated)
    ctx.save();
    ctx.translate(w - 5, h - 5);
    ctx.rotate(Math.PI);
    ctx.font = 'bold 14px Georgia, serif';
    ctx.fillText(rankName, 0, 11);
    ctx.font = '15px Georgia, serif';
    ctx.fillText(symbol, 0, 26);
    ctx.restore();

    // Center symbol (large)
    ctx.font = '32px Georgia, serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(symbol, w / 2, h / 2);

    // For face cards, add letter
    if (rank >= 11) {
      ctx.font = 'bold 18px Georgia, serif';
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.3;
      ctx.fillText(rankName, w / 2, h / 2 - 20);
      ctx.globalAlpha = 1;
    }

    // Wild indicator for 2-rank cards
    if (rank === 2) {
      ctx.font = 'bold 9px Georgia, serif';
      ctx.fillStyle = '#9b59b6';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('WILD', w / 2, h - 5);
    }

    canvas.refresh();
  }

  private generateJokerFace(index: number): void {
    const key = `card_joker_${index}`;
    const w = CARD_WIDTH;
    const h = CARD_HEIGHT;

    const canvas = this.textures.createCanvas(key, w, h);
    if (!canvas) return;
    const ctx = canvas.context;

    // Purple/gold background to make jokers visually distinct
    this.drawRoundedRect(ctx, 0, 0, w, h, 6, '#4a1a6b', '#ffd700');

    // Joker text top
    ctx.font = 'bold 13px Georgia, serif';
    ctx.fillStyle = '#ffd700';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('JOKER', w / 2, 5);

    // Draw a star using canvas paths instead of text (more reliable)
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    const cx = w / 2, cy = h / 2;
    const outerR = 18, innerR = 8;
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const angle = (Math.PI / 2) + (i * Math.PI / 5);
      const px = cx + r * Math.cos(angle);
      const py = cy - r * Math.sin(angle);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    // "WILD" label
    ctx.font = 'bold 10px Georgia, serif';
    ctx.fillStyle = '#ffd700';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('WILD', w / 2, h - 6);

    canvas.refresh();
  }

  private generateCardBack(name: string, color: number): void {
    const key = `card_back_${name}`;
    const w = CARD_WIDTH;
    const h = CARD_HEIGHT;

    const canvas = this.textures.createCanvas(key, w, h);
    if (!canvas) return;
    const ctx = canvas.context;

    const hex = '#' + color.toString(16).padStart(6, '0');
    this.drawRoundedRect(ctx, 0, 0, w, h, 6, hex, '#333333');

    // Diamond pattern
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    for (let x = 8; x < w - 4; x += 10) {
      for (let y = 8; y < h - 4; y += 10) {
        ctx.beginPath();
        ctx.moveTo(x, y - 4);
        ctx.lineTo(x + 4, y);
        ctx.lineTo(x, y + 4);
        ctx.lineTo(x - 4, y);
        ctx.closePath();
        ctx.stroke();
      }
    }

    // Inner border
    this.drawRoundedRect(ctx, 4, 4, w - 8, h - 8, 3, 'transparent', 'rgba(255,255,255,0.3)');

    canvas.refresh();
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    r: number, fill: string, stroke: string,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();

    if (fill && fill !== 'transparent') {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
}
